import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { day, dt, num, inr, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useDataTable, TableToolbar, TablePager } from '../components/DataTable';

const statusBadge = (s) => ({ draft: 'badge-gray', sent: 'badge-gold', partial: 'badge-gold', received: 'badge-green', closed: 'badge-green', cancelled: 'badge-red' }[s] || 'badge-gray');
const urgencyBadge = (u) => ({ overdue: 'badge-red', high: 'badge-red', medium: 'badge-gold', low: 'badge-green' }[u] || 'badge-gray');
const Urgency = ({ level }) => (level && level !== 'none' ? <span className={`badge ${urgencyBadge(level)}`}>{level}</span> : <span className="muted">—</span>);
const toggle = (set, key) => { const n = new Set(set); n.has(key) ? n.delete(key) : n.add(key); return n; };

// ── Consolidate open branch demand → kitchen order / vendor PO ──
const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const Consolidate = ({ con, vendors, onCreated }) => {
  // Selection is per stock-request line; the kitchen order quantity follows the ticked requests.
  const reqKey = (pid, idx) => `${pid}#${idx}`;
  const sumSel = (k, sel) => (k.requests || []).reduce((s, r, idx) => (sel.has(reqKey(k.productId, idx)) ? s + Number(r.kg || 0) : s), 0);
  const initReqSel = () => { const s = new Set(); con.kitchen.forEach((k) => { if (k.suggestKg > 0) (k.requests || []).forEach((_, idx) => s.add(reqKey(k.productId, idx))); }); return s; };
  const [reqSel, setReqSel] = useState(initReqSel);
  const [kQty, setKQty] = useState(() => { const rs = initReqSel(); return Object.fromEntries(con.kitchen.map((k) => [k.productId, r3(sumSel(k, rs))])); });

  const applyReqSel = (k, next) => { setReqSel(next); setKQty((q) => ({ ...q, [k.productId]: r3(sumSel(k, next)) })); };
  const toggleReq = (k, idx) => { const key = reqKey(k.productId, idx); const n = new Set(reqSel); n.has(key) ? n.delete(key) : n.add(key); applyReqSel(k, n); };
  const toggleProduct = (k) => {
    const keys = (k.requests || []).map((_, idx) => reqKey(k.productId, idx));
    const allSel = keys.length > 0 && keys.every((x) => reqSel.has(x));
    const n = new Set(reqSel); keys.forEach((x) => (allSel ? n.delete(x) : n.add(x))); applyReqSel(k, n);
  };
  const reqOn = (k, idx) => reqSel.has(reqKey(k.productId, idx));
  const anyReqOn = (k) => (k.requests || []).some((_, idx) => reqOn(k, idx));

  const [vendorId, setVendorId] = useState('');
  // Vendor lines keyed by product+variant (loose products all share variantId 0).
  const lineKey = (p, l) => `${p.productId}_${l.variantId}`;
  const [vLine, setVLine] = useState(() => { const m = {}; con.vendor.forEach((p) => p.lines.forEach((l) => { m[lineKey(p, l)] = { qty: l.suggest, price: l.unitPrice || 0 }; })); return m; });
  const [vSel, setVSel] = useState(() => new Set(con.vendor.flatMap((p) => p.lines.filter((l) => l.suggest > 0).map((l) => lineKey(p, l)))));
  const [openK, setOpenK] = useState(null);   // kitchen product whose requests are expanded
  const [busy, setBusy] = useState(false);

  const setV = (id, k, v) => setVLine((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const createKitchen = async () => {
    const picks = con.kitchen.filter((k) => anyReqOn(k) && Number(kQty[k.productId]) > 0);
    if (!picks.length) return toast.error('Select at least one request to send');
    setBusy(true);
    // Each product goes as its own kitchen order, created and sent immediately so it
    // reaches the kitchen's Making list right away.
    try {
      for (const k of picks) {
        const reqs = (k.requests || []).filter((_, idx) => reqOn(k, idx)).map((r) => ({ transferItemId: r.transferItemId, qty: r.units }));
        const { data } = await api.post('/procurement', { target: 'kitchen', items: [{ productId: k.productId, variantId: 0, qtyOrdered: Number(kQty[k.productId]), unit: k.unit || 'kg', requests: reqs }] });
        await api.post(`/procurement/${data.order.id}/send`);
      }
      toast.success(`Sent ${picks.length} kitchen order${picks.length > 1 ? 's' : ''}`); onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const createVendor = async () => {
    if (!vendorId) return toast.error('Select a vendor');
    const items = [];
    con.vendor.forEach((p) => p.lines.forEach((l) => {
      const k = lineKey(p, l);
      if (vSel.has(k) && Number(vLine[k]?.qty) > 0)
        items.push({ productId: p.productId, variantId: l.variantId, qtyOrdered: Number(vLine[k].qty), unit: l.unit || 'unit', unitPrice: Number(vLine[k].price || 0) });
    }));
    if (!items.length) return toast.error('Select at least one item with a quantity');
    setBusy(true);
    try { await api.post('/procurement', { target: 'vendor', vendorId: Number(vendorId), items }); toast.success('Purchase order created'); onCreated(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      {/* Kitchen (made in-house) */}
      <div className="card card-pad" style={{ marginBottom: '1.2rem' }}>
        <div className="section-head" style={{ marginBottom: '.4rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Make in the kitchen</h3>
          <button className="btn btn-primary btn-sm" onClick={createKitchen} disabled={busy || !con.kitchen.length}>Send to kitchen</button>
        </div>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th></th><th>Product</th><th>Urgency</th><th className="right">Needed</th><th className="right">In warehouse</th><th className="right">On order</th><th className="right">Order</th></tr></thead>
            <tbody>
              {con.kitchen.map((k) => {
                const onOrderNote = !(k.suggestKg > 0);   // demand already covered by stock / open orders
                const reqs = k.requests || [];
                const open = openK === k.productId;
                const selCount = reqs.filter((_, i) => reqOn(k, i)).length;
                return (
                <React.Fragment key={k.productId}>
                <tr>
                  <td><input type="checkbox" checked={anyReqOn(k)} title="Select / clear all requests for this product" onChange={() => toggleProduct(k)} /></td>
                  <td>
                    <strong>{k.product}</strong>{k.loose && <span className="muted small"> · by {k.unit || 'kg'}</span>}{onOrderNote && <span className="muted small"> · on order</span>}
                    {reqs.length > 0 && <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 8 }} onClick={() => setOpenK(open ? null : k.productId)}>{open ? '▲' : '▼'} {selCount}/{reqs.length} request{reqs.length > 1 ? 's' : ''}</button>}
                  </td>
                  <td><Urgency level={k.urgency} /></td>
                  <td className="right">{num(k.demandKg)} {k.unit || 'kg'}</td>
                  <td className="right">{num(k.onHand)}</td>
                  <td className="right">{k.onOrder > 0 ? num(k.onOrder) : '—'}</td>
                  <td className="right">
                    <div className="flex" style={{ gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                      <input type="number" min="0" step="0.001" style={{ width: 100 }} value={kQty[k.productId]} onChange={(e) => setKQty((p) => ({ ...p, [k.productId]: e.target.value }))} />
                      <span className="muted small">{k.unit || 'kg'}</span>
                    </div>
                  </td>
                </tr>
                {open && reqs.length > 0 && (
                  <tr><td colSpan={7} style={{ background: '#faf7ec' }}>
                    <table className="grid" style={{ margin: '.3rem 0' }}>
                      <thead><tr><th></th><th>Request</th><th>Branch</th><th>Pack</th><th className="right">Qty</th><th className="right">Kg</th><th>Needed by</th></tr></thead>
                      <tbody>
                        {reqs.map((r, i) => (
                          <tr key={i}>
                            <td><input type="checkbox" checked={reqOn(k, i)} onChange={() => toggleReq(k, i)} /></td>
                            <td className="mono">{r.code}</td><td>{r.branch}</td><td>{r.variant}</td>
                            <td className="right">{num(r.units)}</td><td className="right">{num(r.kg)} {k.unit || 'kg'}</td>
                            <td className="muted">{r.needByDate ? day(r.needByDate) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="muted small">Ticked requests set the order quantity ({num(kQty[k.productId] || 0)} {k.unit || 'kg'}).</p>
                  </td></tr>
                )}
                </React.Fragment>
                );
              })}
              {!con.kitchen.length && <tr><td colSpan={7} className="empty">No made-in-house items awaiting fulfilment.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vendor (bought) */}
      <div className="card card-pad" style={{ marginBottom: '1.2rem' }}>
        <div className="section-head" style={{ marginBottom: '.4rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Buy from a vendor</h3>
          <div className="flex gap" style={{ alignItems: 'center' }}>
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} style={{ width: 200 }}>
              <option value="">— select vendor —</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={createVendor} disabled={busy || !con.vendor.length}>Create PO</button>
          </div>
        </div>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th></th><th>Product / pack</th><th className="right">Needed</th><th className="right">In warehouse</th><th className="right">On order</th><th className="right">Order (units)</th><th className="right">Unit price</th></tr></thead>
            <tbody>
              {con.vendor.flatMap((p) => p.lines.map((l) => {
                const k = lineKey(p, l);
                const done = !(l.suggest > 0);   // already covered by stock / open orders
                return (
                <tr key={k} style={done ? { opacity: 0.55 } : undefined}>
                  <td><input type="checkbox" checked={vSel.has(k)} disabled={done} title={done ? 'Already on order / in stock' : ''} onChange={() => setVSel((s) => toggle(s, k))} /></td>
                  <td><strong>{p.product}</strong> <span className="muted small">{l.label}{done && ' · on order'}</span></td>
                  <td className="right">{num(l.units)}</td>
                  <td className="right">{num(l.onHand)}</td>
                  <td className="right">{l.onOrder > 0 ? num(l.onOrder) : '—'}</td>
                  <td className="right"><input type="number" min="0" step="0.001" style={{ width: 90 }} value={vLine[k]?.qty} onChange={(e) => setV(k, 'qty', e.target.value)} /></td>
                  <td className="right"><input type="number" min="0" step="0.01" style={{ width: 90 }} value={vLine[k]?.price} onChange={(e) => setV(k, 'price', e.target.value)} /></td>
                </tr>
                );
              }))}
              {!con.vendor.length && <tr><td colSpan={7} className="empty">No bought items awaiting fulfilment.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="muted small">Bought products are set via the product's <strong>Source = Bought</strong> field.</p>
      </div>
    </>
  );
};

// ── Orders list ──
const OrdersList = ({ orders, canWrite, onOpen, onChanged }) => {
  const [busyId, setBusyId] = useState(null);
  const acceptableOf = (it, target) => Math.max(0, target === 'vendor' ? Number(it.qtyOrdered) - Number(it.qtyReceived) : Number(it.qtyProduced || 0) - Number(it.qtyReceived || 0));
  const orderAcceptable = (o) => (o.items || []).reduce((s, it) => s + acceptableOf(it, o.target), 0);
  const productsLabel = (o) => (o.items || []).map((it) => `${it.product?.name || `#${it.itemId}`}${it.variant ? ` ${it.variant.weightLabel}` : ''} — ${num(it.qtyOrdered)} ${it.unit}`).join(', ');
  const sum = (o, f) => (o.items || []).reduce((s, it) => s + Number(f(it) || 0), 0);
  const toLabel = (o) => (o.target === 'vendor' ? (o.vendor?.name || 'Vendor') : 'Central Kitchen');
  const list = useDataTable(orders, {
    searchPlaceholder: 'Search order code, product…',
    searchKeys: ['code', (o) => toLabel(o), (o) => productsLabel(o), 'status'],
    filters: [
      { key: 'status', label: 'Status', value: (o) => o.status },
      { key: 'target', label: 'To', value: (o) => toLabel(o) },
    ],
  });
  const accept = async (e, o) => {
    e.stopPropagation();
    const items = (o.items || []).map((it) => ({ id: it.id, qty: acceptableOf(it, o.target) })).filter((x) => x.qty > 0);
    if (!items.length) return;
    setBusyId(o.id);
    try { const { data } = await api.post(`/procurement/${o.id}/receive`, { items }); toast.success(`Order ${data.order.status}`); onChanged(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusyId(null); }
  };
  return (
    <div className="card card-pad">
      <TableToolbar dt={list} />
      <div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Order</th><th>To</th><th>Products &amp; quantity</th><th className="right">Produced / Received</th><th className="right">Raised</th><th className="right">Received</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {list.rows.map((o) => {
              const acc = orderAcceptable(o);
              const ready = acc > 0 && ['sent', 'partial'].includes(o.status);
              return (
                <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(o)}>
                  <td className="mono">{o.code}</td>
                  <td>{o.target === 'vendor' ? (o.vendor?.name || 'Vendor') : 'Central Kitchen'}</td>
                  <td>{productsLabel(o)}</td>
                  <td className="right">{o.target === 'vendor' ? '—' : `${num(sum(o, (it) => it.qtyProduced))} / ${num(sum(o, (it) => it.qtyReceived))}`}</td>
                  <td className="muted right">{dt(o.sentAt || o.createdAt)}</td>
                  <td className="muted right">{o.receivedAt ? dt(o.receivedAt) : '—'}</td>
                  <td>
                    <span className={`badge ${statusBadge(o.status)}`}>{o.status}</span>
                    {ready && <span className="badge badge-green" style={{ marginLeft: 6 }}>ready to accept</span>}
                  </td>
                  <td className="right">
                    {canWrite && ready
                      ? <button className="btn btn-gold btn-sm" disabled={busyId === o.id} onClick={(e) => accept(e, o)}>{busyId === o.id ? '…' : (o.target === 'vendor' ? 'Receive' : 'Accept')}</button>
                      : <span className="muted">›</span>}
                  </td>
                </tr>
              );
            })}
            {!list.rows.length && <tr><td colSpan={8} className="empty">{list.isFiltered ? 'No orders match your search.' : 'No procurement orders yet.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <TablePager dt={list} />
    </div>
  );
};

// ── Order detail: send / receive / cancel ──
const OrderDrawer = ({ order, canWrite, onClose, onChanged }) => {
  const [o, setO] = useState(order);
  const isVendor = o.target === 'vendor';
  // Kitchen: accept up to what's produced-but-not-yet-accepted. Vendor: up to outstanding.
  const acceptable = (it) => Math.max(0, isVendor
    ? Number(it.qtyOrdered) - Number(it.qtyReceived)
    : Number(it.qtyProduced || 0) - Number(it.qtyReceived || 0));
  const [recv, setRecv] = useState(() => Object.fromEntries((order.items || []).map((it) => [it.id, acceptable(it)])));
  const [busy, setBusy] = useState(false);

  const act = async (path, body) => {
    setBusy(true);
    try { const { data } = await api.post(`/procurement/${o.id}/${path}`, body); setO(data.order); setRecv(Object.fromEntries((data.order.items || []).map((it) => [it.id, acceptable(it)]))); onChanged(data.order); toast.success(`Order ${data.order.status}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const label = (it) => (it.variant ? `${it.product?.name} — ${it.variant.weightLabel}` : `${it.product?.name} (${it.unit || 'kg'})`);
  const hasAcceptable = (o.items || []).some((it) => acceptable(it) > 0);
  const canAccept = ['sent', 'partial'].includes(o.status) && canWrite && (isVendor || hasAcceptable);
  const receiveBody = { items: (o.items || []).map((it) => ({ id: it.id, qty: Number(recv[it.id]) || 0 })).filter((x) => x.qty > 0) };

  return (
    <Modal title={`${isVendor ? 'PO' : 'Kitchen order'} ${o.code}`} onClose={onClose} wide
      footer={<>
        <button className="btn btn-outline" onClick={onClose}>Close</button>
        {canWrite && o.status === 'draft' && <button className="btn btn-primary" onClick={() => act('send')} disabled={busy}>Send</button>}
        {canAccept && <button className="btn btn-gold" onClick={() => act('receive', receiveBody)} disabled={busy || !receiveBody.items.length}>{isVendor ? 'Receive into warehouse' : 'Accept & receive'}</button>}
        {canWrite && !['received', 'closed', 'cancelled'].includes(o.status) && <button className="btn btn-danger" onClick={() => act('cancel')} disabled={busy}>Cancel</button>}
      </>}>
      <div className="flex" style={{ justifyContent: 'space-between' }}>
        <div>To <strong>{isVendor ? (o.vendor?.name || 'Vendor') : 'Central Kitchen'}</strong>{o.poNumber ? ` · PO ${o.poNumber}` : ''}{o.expectedDate ? ` · expected ${day(o.expectedDate)}` : ''}</div>
        <span className={`badge ${statusBadge(o.status)}`}>{o.status}</span>
      </div>
      <div className="muted small mt">Raised {dt(o.sentAt || o.createdAt)}{o.receivedAt ? ` · Received ${dt(o.receivedAt)}` : ''}</div>
      {!isVendor && ['sent', 'partial'].includes(o.status) && !hasAcceptable && <p className="muted small mt">Waiting for the kitchen to produce this order. Accept it once batches are marked done.</p>}
      {canAccept && <p className="muted small mt">Accepting moves the {isVendor ? 'delivered' : 'produced'} quantity into warehouse stock and advances the order — it closes once every line is fully received.</p>}
      <div className="table-wrap mt">
        <table className="grid">
          <thead><tr><th>Product</th><th className="right">Ordered</th>{!isVendor && <th className="right">Produced</th>}<th className="right">Received</th>{isVendor && <th className="right">Unit price</th>}{canAccept && <th className="right">Accept now</th>}</tr></thead>
          <tbody>
            {(o.items || []).map((it) => (
              <tr key={it.id}>
                <td>{label(it)}</td>
                <td className="right">{num(it.qtyOrdered)} {it.unit}</td>
                {!isVendor && <td className="right">{num(it.qtyProduced || 0)}</td>}
                <td className="right">{num(it.qtyReceived)}</td>
                {isVendor && <td className="right">{inr(it.unitPrice)}</td>}
                {canAccept && <td className="right">
                  <input type="number" min="0" style={{ width: 90 }} value={recv[it.id]} onChange={(e) => setRecv((p) => ({ ...p, [it.id]: e.target.value }))} />
                  {Number(recv[it.id]) > acceptable(it) && <div className="neg" style={{ fontSize: '.75rem' }}>max {num(acceptable(it))}</div>}
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

const Procurement = () => {
  const { can } = useAuth();
  const canWrite = can('procurement:write') || can('transfers:write');
  const [tab, setTab] = useState('consolidate');
  const [con, setCon] = useState(null);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCon = () => api.get('/procurement/consolidation').then(({ data }) => setCon(data)).catch((e) => toast.error(apiError(e)));
  const loadOrders = () => api.get('/procurement').then(({ data }) => setOrders(data.orders || [])).catch(() => {});
  const load = () => { setLoading(true); Promise.all([loadCon(), loadOrders()]).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get('/suppliers').then(({ data }) => setVendors(data.suppliers || [])).catch(() => {}); }, []);

  // Procurement (consolidate & order from kitchen/vendor) is a warehouse-manager task.
  if (!canWrite) return (
    <div className="card card-pad" style={{ marginTop: '1rem' }}>
      <h2>Procurement</h2>
      <p className="muted">Procurement is available to the warehouse manager only.</p>
    </div>
  );

  return (
    <div>
      <p className="muted small" style={{ marginTop: 0 }}>Consolidate open branch requests and order from the kitchen or a vendor. Received stock lands in the warehouse, which then fulfils each branch request.</p>

      <div className="quick-sizes" style={{ margin: '.4rem 0 1rem' }}>
        <button className={`chip ${tab === 'consolidate' ? 'chip-on' : ''}`} onClick={() => setTab('consolidate')}>Consolidate</button>
        <button className={`chip ${tab === 'orders' ? 'chip-on' : ''}`} onClick={() => setTab('orders')}>Orders {orders.length ? `(${orders.length})` : ''}</button>
      </div>

      {loading && <div className="spinner" />}
      {!loading && tab === 'consolidate' && con && <Consolidate con={con} vendors={vendors} onCreated={() => { load(); setTab('orders'); }} />}
      {!loading && tab === 'orders' && <OrdersList orders={orders} canWrite={canWrite} onOpen={setDetail} onChanged={() => { loadOrders(); loadCon(); }} />}
      {detail && <OrderDrawer order={detail} canWrite={canWrite} onClose={() => setDetail(null)} onChanged={() => { loadOrders(); loadCon(); }} />}
    </div>
  );
};

export default Procurement;
